
import { EXPERIMENT, startExperiment } from 'lib/optimize'
import { useEffect, useMemo, useRef, useState } from 'react'
import { reportError } from 'services/errorReportingService'

import useBrowserInteractionTime from './useBrowserInteractionTime'

interface Params {
  // Change preventDiffKey to prevent the footer from ever showing,
  // unless it has already been shown
  preventDiffKey?: string
  optimizeActivation?: EXPERIMENT;
  idleTimeout: number | undefined;
  shouldTrack?: boolean;
  // Change timerId to reset the idle timer and hide the footer
  timerId?: number;
}

const DEFAULT_TIMEOUT = 7500

function useLereYmalFooterIdleTriggerDetector({
  preventDiffKey = '',
  optimizeActivation,
  idleTimeout,
  shouldTrack = true,
  timerId = 1,
}: Params) {
  const idleThreshold = useMemo(() => {
    try {
      const val = Number(idleTimeout)
      return Number.isFinite(val) ? val : DEFAULT_TIMEOUT
    } catch (e) {
      reportError(e)
    }
    return DEFAULT_TIMEOUT
  }, [idleTimeout])

  const [idleTimePassed, setIdleTimePassed] = useState(false)
  const { active } = useBrowserInteractionTime(idleThreshold, timerId, shouldTrack)

  useEffect(() => {
    setIdleTimePassed(false)
  }, [timerId])

  // If this key changes, the footer will not show, if it was shown before
  const preventDiffKeyRef = useRef(preventDiffKey)
  const preventYmalTrigger = useRef(false)

  useEffect(() => {
    if (preventDiffKey !== preventDiffKeyRef.current) {
      preventYmalTrigger.current = true
    }
  }, [preventDiffKey])

  useEffect(() => {
    if (!active && !preventYmalTrigger.current) {
      setIdleTimePassed(true)
    }
  }, [active, setIdleTimePassed])

  const hasTriggeredExperiment = useRef(false)
  useEffect(() => {
    if (optimizeActivation && idleTimePassed && !hasTriggeredExperiment.current) {
      hasTriggeredExperiment.current = true
      startExperiment(optimizeActivation)
    }
  }, [idleTimePassed, optimizeActivation])

  return {
    idleTimePassed,
  }
}

export default useLereYmalFooterIdleTriggerDetector
